import React, { FC, forwardRef } from 'react';
import { useLocation } from 'react-router';
import styled from 'styled-components';
import { FieldOptionBase, StatusFieldOption, TaskType } from 'api/tasks';
import { StatusPickerContext } from 'components/atoms/status-picker/context';
import { StatusPickerOverlay } from 'components/atoms/status-picker/status-picker-overlay';
import { Task } from 'data/tasks';
import { useTranslation } from 'hooks/use-translation';
import { FieldHelper } from 'utils/field';
import { SingleSelectField } from '../fields';
import { EditableProps } from './editable-props';
import { NoValue } from './no-value';
import { SelectFieldOption } from './option';
import { AddTask } from './task/add';

interface Props extends Partial<EditableProps<number>> {
  field: SingleSelectField;
  provisional: boolean;
  onCreate?: (task: Task) => void;
  onCreateMultiple?: (tasks: Task[]) => void;
  type: TaskType;
}

const StatusPicker = styled(StatusPickerOverlay)`
  padding: var(--layout-02) 0 var(--spacing-03) !important;

  [role='listbox'] {
    width: 100%;
  }
`;

export const TaskInfoFieldValue: FC<Props> = forwardRef(
  ({
    type,
    field,
    editable,
    onChange,
    id,
    onCreate,
    onCreateMultiple,
    provisional,
  }) => {
    const { pathname } = useLocation();

    const getStatusHeader = () => {
      if (pathname === '/projects') {
        return 'project_status';
      }
      if (pathname === '/sprints') {
        return 'sprint_status';
      }

      return 'task_status';
    };

    const taskStatusHeader = useTranslation(getStatusHeader());
    const {
      options: { activeOptions, allOptions },
    } = FieldHelper(field);

    return !editable ? (
      field.value == null ? (
        <NoValue />
      ) : (
        <SelectFieldOption
          option={field.metaData.options.find((x) => x.value === field.value)!}
        />
      )
    ) : provisional ? (
      <AddTask
        id={id!}
        onCreate={onCreate}
        onCreateMultiple={onCreateMultiple}
        autoFocus
      />
    ) : (
      <StatusPickerContext.Provider value={{ type, isOpen: true }}>
        <StatusPicker
          header={taskStatusHeader}
          options={activeOptions as StatusFieldOption[]}
          value={
            allOptions.find(
              (x) => x.value === field?.value,
            ) as StatusFieldOption
          }
          onChange={(latest: FieldOptionBase) => onChange?.(latest?.value)}
        />
      </StatusPickerContext.Provider>
    );
  },
);
